export interface ErrorBody {
  error?: string;
  [key: string]: unknown;
}

export type MessageResolver = string | ((body: ErrorBody) => string);
export type ErrorMessageMap = { [status: number]: MessageResolver; default: MessageResolver };

// Safe universal parser: text -> JSON.parse; on a non-JSON body, preserve the raw text as `error`.
export async function parseErrorBody(response: Response): Promise<ErrorBody> {
  let text = '';
  try {
    text = await response.text();
    return text ? JSON.parse(text) : {};
  } catch {
    return { error: text || 'Unknown server error' };
  }
}

// Messages are unchanged from the plain-Error era; the status is what callers branch on.
export class HttpError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'HttpError';
  }
}

export class UnauthorizedError extends HttpError {
  constructor() {
    super('Authentication required', 401);
    this.name = 'UnauthorizedError';
  }
}

// 401 takes precedence over the message map (folds in the old throwForUnauthorizedMutation).
// Status >= 500 logs before throwing, preserving server-error observability.
export async function throwMappedError(response: Response, messages: ErrorMessageMap): Promise<never> {
  if (response.status === 401) {
    throw new UnauthorizedError();
  }
  const body = await parseErrorBody(response);
  if (response.status >= 500) {
    console.error(`Server error (${response.status}):`, body);
  }
  const resolver = messages[response.status] ?? messages.default;
  throw new HttpError(typeof resolver === 'function' ? resolver(body) : resolver, response.status);
}
